import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { academicService } from '../services/academicService'
import { studentService } from '../services/studentService'

const emptyClassForm = {
  name: '',
  termId: '',
  teacherId: '',
  secondaryTeacherId: '',
  period: '',
  subjectIds: []
}

export default function AdminAcademicPanel() {
  const navigate = useNavigate()
  const [isReady, setIsReady] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [terms, setTerms] = useState([])
  const [subjects, setSubjects] = useState([])
  const [teachers, setTeachers] = useState([])
  const [classes, setClasses] = useState([])
  const [students, setStudents] = useState([])
  const [classForm, setClassForm] = useState(emptyClassForm)
  const [termForm, setTermForm] = useState({ name: '', startsOn: '', endsOn: '', isCurrent: false })
  const [subjectForm, setSubjectForm] = useState({ name: '', notes: '' })
  const [selectedClassId, setSelectedClassId] = useState(null)
  const [roster, setRoster] = useState([])
  const [selectedStudentIds, setSelectedStudentIds] = useState([])
  const [studentSearch, setStudentSearch] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [classEdit, setClassEdit] = useState({ name: '', period: '' })
  const [editingSubjectId, setEditingSubjectId] = useState(null)
  const [editingSubjectName, setEditingSubjectName] = useState('')
  const [subjectToAdd, setSubjectToAdd] = useState('')

  const selectedClass = useMemo(
    () => classes.find((item) => item.id === selectedClassId) || null,
    [classes, selectedClassId]
  )

  const loadData = async () => {
    const ready = await academicService.isReady()
    setIsReady(ready)
    if (!ready) {
      setLoading(false)
      return
    }

    const [termRows, subjectRows, teacherRows, classRows, studentRows] = await Promise.all([
      academicService.listTerms(),
      academicService.listSubjects(),
      academicService.listTeachers(),
      academicService.listClasses(),
      studentService.getAllStudents()
    ])

    setTerms(termRows)
    setSubjects(subjectRows)
    setTeachers(teacherRows)
    setClasses(classRows)
    setStudents(studentRows)

    const currentTerm = termRows.find((term) => term.is_current)
    setClassForm((prev) => ({
      ...prev,
      termId: prev.termId || currentTerm?.id || ''
    }))
  }

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true)
        setError(null)
        await loadData()
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    run()
  }, [])

  useEffect(() => {
    const loadRoster = async () => {
      if (!selectedClass) {
        setRoster([])
        return
      }
      try {
        const rows = await academicService.listClassStudents(selectedClass.id)
        setRoster(rows)
      } catch (err) {
        setError(err.message)
      }
    }

    loadRoster()
  }, [selectedClass])

  useEffect(() => {
    if (!selectedClass) {
      setClassEdit({ name: '', period: '' })
      setSubjectToAdd('')
      return
    }
    setClassEdit({
      name: selectedClass.name || '',
      period: selectedClass.period || ''
    })
  }, [selectedClass])

  const handleTeacherChange = (teacherId) => {
    const teacher = teachers.find((item) => String(item.id) === String(teacherId))
    setClassForm((prev) => ({
      ...prev,
      teacherId,
      name: prev.name.trim() ? prev.name : teacher ? `${teacher.name} class` : prev.name
    }))
  }

  const handleToggleSubject = (subjectId) => {
    setClassForm((prev) => {
      const exists = prev.subjectIds.includes(subjectId)
      return {
        ...prev,
        subjectIds: exists
          ? prev.subjectIds.filter((id) => id !== subjectId)
          : [...prev.subjectIds, subjectId]
      }
    })
  }

  const handleCreateTerm = async (event) => {
    event.preventDefault()
    setIsSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await academicService.createTerm(termForm)
      setTermForm({ name: '', startsOn: '', endsOn: '', isCurrent: false })
      setSuccess('Term saved.')
      await loadData()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCreateSubject = async (event) => {
    event.preventDefault()
    setIsSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await academicService.createSubject(subjectForm)
      setSubjectForm({ name: '', notes: '' })
      setSuccess('Subject added to the catalog.')
      await loadData()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleCreateClass = async (event) => {
    event.preventDefault()
    setIsSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const created = await academicService.createClass({
        name: classForm.name,
        termId: Number(classForm.termId),
        teacherId: Number(classForm.teacherId),
        secondaryTeacherId: classForm.secondaryTeacherId ? Number(classForm.secondaryTeacherId) : null,
        period: classForm.period,
        subjectIds: classForm.subjectIds.map((id) => Number(id))
      })
      setClassForm({
        ...emptyClassForm,
        termId: classForm.termId
      })
      setSelectedClassId(created.id)
      setSuccess(`${created.name} created. Gradebook is ready at this class.`)
      await loadData()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleUpdateSubject = async (subject) => {
    setIsSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await academicService.updateSubject(subject.id, {
        name: editingSubjectName,
        notes: subject.notes
      })
      setEditingSubjectId(null)
      setEditingSubjectName('')
      setSuccess('Subject title updated.')
      await loadData()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleSaveClassDetails = async (event) => {
    event.preventDefault()
    if (!selectedClass) return
    setIsSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await academicService.updateClass(selectedClass.id, classEdit)
      setSuccess('Class updated.')
      await loadData()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleAddSubjectToClass = async () => {
    if (!selectedClass || !subjectToAdd) return
    setIsSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await academicService.addClassSubject(selectedClass.id, Number(subjectToAdd))
      setSubjectToAdd('')
      setSuccess('Subject added to class.')
      await loadData()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleRemoveSubjectFromClass = async (subjectId) => {
    if (!selectedClass) return
    setIsSaving(true)
    setError(null)
    try {
      await academicService.removeClassSubject(selectedClass.id, subjectId)
      await loadData()
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleEnrollSelected = async () => {
    if (!selectedClass || selectedStudentIds.length === 0) return
    setIsSaving(true)
    setError(null)
    setSuccess(null)
    try {
      await academicService.enrollStudents(
        selectedClass.id,
        selectedStudentIds,
        selectedClass.legacy_level_code
      )
      setSelectedStudentIds([])
      setSuccess('Students enrolled in the class and gradebook.')
      const rows = await academicService.listClassStudents(selectedClass.id)
      setRoster(rows)
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const handleRemoveStudent = async (studentId) => {
    if (!selectedClass) return
    if (!window.confirm('Remove this student from the class?')) return
    setIsSaving(true)
    setError(null)
    try {
      await academicService.unenrollStudent(selectedClass.id, studentId, selectedClass.legacy_level_code)
      const rows = await academicService.listClassStudents(selectedClass.id)
      setRoster(rows)
    } catch (err) {
      setError(err.message)
    } finally {
      setIsSaving(false)
    }
  }

  const rosterIds = new Set(roster.map((student) => student.id))
  const enrollableStudents = students.filter((student) => {
    if (rosterIds.has(student.id)) return false
    if (!studentSearch.trim()) return true
    const haystack = `${student.name} ${student.code || ''} ${student.email || ''}`.toLowerCase()
    return haystack.includes(studentSearch.toLowerCase())
  })

  if (loading) {
    return (
      <div className="py-12 text-center text-neutral-500">
        Loading academic setup...
      </div>
    )
  }

  if (isReady === false) {
    return (
      <div className="bg-white rounded-2xl border border-amber-200 p-6">
        <h2 className="text-lg font-bold text-neutral-900 mb-2">Run the academic migration</h2>
        <p className="text-sm text-neutral-600 mb-4">
          New class and transcript tables are not in the database yet. In Supabase, open SQL Editor and run
          {' '}<code className="text-xs bg-neutral-100 px-1 py-0.5 rounded">sql/20260912_classes_and_transcripts.sql</code>.
          Existing levels and grades are not changed.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm" role="alert">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-4 text-sm">
          {success}
        </div>
      )}

      <form onSubmit={handleCreateClass} className="bg-white rounded-2xl border border-neutral-200 p-5 space-y-4">
        <div>
          <h2 className="text-lg font-bold text-neutral-900">Create a class</h2>
          <p className="text-sm text-neutral-500">
            Teacher + term + one or more subjects. Students enroll in this class. A backing gradebook level is created automatically.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block text-sm font-medium text-neutral-700">
            Teacher
            <select
              required
              value={classForm.teacherId}
              onChange={(event) => handleTeacherChange(event.target.value)}
              className="input w-full mt-1"
            >
              <option value="">Select teacher</option>
              {teachers.map((teacher) => (
                <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
              ))}
            </select>
          </label>

          <label className="block text-sm font-medium text-neutral-700">
            Secondary teacher
            <select
              value={classForm.secondaryTeacherId}
              onChange={(event) => setClassForm((prev) => ({ ...prev, secondaryTeacherId: event.target.value }))}
              className="input w-full mt-1"
            >
              <option value="">None</option>
              {teachers
                .filter((teacher) => String(teacher.id) !== String(classForm.teacherId))
                .map((teacher) => (
                  <option key={teacher.id} value={teacher.id}>{teacher.name}</option>
                ))}
            </select>
          </label>

          <label className="block text-sm font-medium text-neutral-700">
            Term
            <select
              required
              value={classForm.termId}
              onChange={(event) => setClassForm((prev) => ({ ...prev, termId: event.target.value }))}
              className="input w-full mt-1"
            >
              <option value="">Select term</option>
              {terms.map((term) => (
                <option key={term.id} value={term.id}>
                  {term.name}{term.is_current ? ' (current)' : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="block text-sm font-medium text-neutral-700">
            Period
            <input
              type="text"
              value={classForm.period}
              onChange={(event) => setClassForm((prev) => ({ ...prev, period: event.target.value }))}
              className="input w-full mt-1"
              placeholder="e.g. Saturday 2:00–5:00"
            />
          </label>
        </div>

        <label className="block text-sm font-medium text-neutral-700">
          Class name
          <input
            required
            type="text"
            value={classForm.name}
            onChange={(event) => setClassForm((prev) => ({ ...prev, name: event.target.value }))}
            className="input w-full mt-1"
            placeholder="Macalin Uthman class"
          />
        </label>

        <fieldset>
          <legend className="text-sm font-medium text-neutral-700 mb-2">Subjects</legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto border border-neutral-200 rounded-xl p-3">
            {subjects.map((subject) => {
              const checked = classForm.subjectIds.includes(subject.id)
              return (
                <label key={subject.id} className="flex items-start gap-2 text-sm text-neutral-800">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleToggleSubject(subject.id)}
                    className="mt-1"
                  />
                  <span>{subject.name}</span>
                </label>
              )
            })}
          </div>
        </fieldset>

        <button type="submit" className="btn-primary" disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Create class'}
        </button>
      </form>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          <h2 className="text-lg font-bold text-neutral-900">This model’s classes</h2>
          {classes.length === 0 ? (
            <p className="text-sm text-neutral-500">No term classes yet. Past semester levels stay under the Classes tab.</p>
          ) : (
            classes.map((classItem) => (
              <button
                key={classItem.id}
                type="button"
                onClick={() => setSelectedClassId(classItem.id)}
                className={`w-full text-left bg-white rounded-2xl border p-4 ${
                  selectedClassId === classItem.id ? 'border-primary-500 ring-2 ring-primary-100' : 'border-neutral-200'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-bold text-neutral-900">{classItem.name}</p>
                    <p className="text-sm text-neutral-500">
                      {classItem.term?.name} · {classItem.teacher?.name}
                      {classItem.secondary_teacher ? ` & ${classItem.secondary_teacher.name}` : ''}
                    </p>
                    <p className="text-sm text-neutral-700 mt-2">
                      {classItem.subjects.map((subject) => subject.name).join(' · ') || 'No subjects'}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    classItem.status === 'completed'
                      ? 'bg-neutral-200 text-neutral-700'
                      : 'bg-green-100 text-green-800'
                  }`}>
                    {classItem.status}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>

        <div className="space-y-4">
          <form onSubmit={handleCreateTerm} className="bg-white rounded-2xl border border-neutral-200 p-4 space-y-3">
            <h3 className="font-bold text-neutral-900">Add term</h3>
            <input
              required
              className="input w-full"
              placeholder="Spring 2027"
              value={termForm.name}
              onChange={(event) => setTermForm((prev) => ({ ...prev, name: event.target.value }))}
            />
            <input
              type="date"
              className="input w-full"
              value={termForm.startsOn}
              onChange={(event) => setTermForm((prev) => ({ ...prev, startsOn: event.target.value }))}
            />
            <input
              type="date"
              className="input w-full"
              value={termForm.endsOn}
              onChange={(event) => setTermForm((prev) => ({ ...prev, endsOn: event.target.value }))}
            />
            <label className="flex items-center gap-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={termForm.isCurrent}
                onChange={(event) => setTermForm((prev) => ({ ...prev, isCurrent: event.target.checked }))}
              />
              Set as current term
            </label>
            <button type="submit" className="btn-secondary w-full" disabled={isSaving}>Save term</button>
          </form>

          <form onSubmit={handleCreateSubject} className="bg-white rounded-2xl border border-neutral-200 p-4 space-y-3">
            <h3 className="font-bold text-neutral-900">Subjects</h3>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {subjects.map((subject) => (
                <div key={subject.id} className="flex items-center gap-2">
                  {editingSubjectId === subject.id ? (
                    <>
                      <input
                        className="input flex-1"
                        value={editingSubjectName}
                        onChange={(event) => setEditingSubjectName(event.target.value)}
                        aria-label={`Rename ${subject.name}`}
                      />
                      <button
                        type="button"
                        className="text-sm text-primary-700"
                        onClick={() => handleUpdateSubject(subject)}
                        disabled={isSaving}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="text-sm text-neutral-500"
                        onClick={() => {
                          setEditingSubjectId(null)
                          setEditingSubjectName('')
                        }}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="flex-1 text-sm text-neutral-800">
                        {subject.name}
                        {subject.notes ? ` · ${subject.notes}` : ''}
                      </span>
                      <button
                        type="button"
                        className="text-sm text-primary-700"
                        onClick={() => {
                          setEditingSubjectId(subject.id)
                          setEditingSubjectName(subject.name)
                        }}
                      >
                        Edit
                      </button>
                    </>
                  )}
                </div>
              ))}
            </div>
            <input
              required
              className="input w-full"
              placeholder="New subject title"
              value={subjectForm.name}
              onChange={(event) => setSubjectForm((prev) => ({ ...prev, name: event.target.value }))}
            />
            <input
              className="input w-full"
              placeholder="Notes (optional)"
              value={subjectForm.notes}
              onChange={(event) => setSubjectForm((prev) => ({ ...prev, notes: event.target.value }))}
            />
            <button type="submit" className="btn-secondary w-full" disabled={isSaving}>Add subject</button>
          </form>
        </div>
      </div>

      {selectedClass && (
        <section className="bg-white rounded-2xl border border-neutral-200 p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h2 className="text-lg font-bold text-neutral-900">Edit class</h2>
              <p className="text-sm text-neutral-500">{selectedClass.term?.name}</p>
            </div>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => navigate(`/level/${selectedClass.legacy_level_code}`)}
            >
              Open gradebook
            </button>
          </div>

          <form onSubmit={handleSaveClassDetails} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block text-sm font-medium text-neutral-700">
              Class name
              <input
                required
                className="input w-full mt-1"
                value={classEdit.name}
                onChange={(event) => setClassEdit((prev) => ({ ...prev, name: event.target.value }))}
              />
            </label>
            <label className="block text-sm font-medium text-neutral-700">
              Period
              <input
                className="input w-full mt-1"
                value={classEdit.period}
                onChange={(event) => setClassEdit((prev) => ({ ...prev, period: event.target.value }))}
              />
            </label>
            <button type="submit" className="btn-primary sm:col-span-2" disabled={isSaving}>
              Save class
            </button>
          </form>

          <div>
            <h3 className="font-semibold text-neutral-900 mb-2">Subjects in this class</h3>
            {selectedClass.subjects.length === 0 ? (
              <p className="text-sm text-neutral-500 mb-3">No subjects yet. Add one below.</p>
            ) : (
              <ul className="divide-y divide-neutral-100 mb-3">
                {selectedClass.subjects.map((subject) => (
                  <li key={subject.id} className="py-2 flex items-center justify-between gap-3">
                    <span className="text-sm text-neutral-800">{subject.name}</span>
                    <button
                      type="button"
                      className="text-sm text-red-600"
                      onClick={() => handleRemoveSubjectFromClass(subject.id)}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                className="input flex-1"
                value={subjectToAdd}
                onChange={(event) => setSubjectToAdd(event.target.value)}
                aria-label="Add subject to class"
              >
                <option value="">Add a subject</option>
                {subjects
                  .filter((subject) => !selectedClass.subjects.some((item) => item.id === subject.id))
                  .map((subject) => (
                    <option key={subject.id} value={subject.id}>{subject.name}</option>
                  ))}
              </select>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleAddSubjectToClass}
                disabled={isSaving || !subjectToAdd}
              >
                Add subject
              </button>
            </div>
          </div>

          <div>
            <h3 className="font-semibold text-neutral-900 mb-2">Roster ({roster.length})</h3>
            {roster.length === 0 ? (
              <p className="text-sm text-neutral-500">No students enrolled yet.</p>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {roster.map((student) => (
                  <li key={student.id} className="py-2 flex items-center justify-between gap-3">
                    <div>
                      <p className="font-medium text-neutral-900">{student.name}</p>
                      <p className="text-xs text-neutral-500">{student.code} · {student.enrollmentStatus}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="text-sm text-primary-700"
                        onClick={() => navigate(`/dashboard/transcript/${student.id}`)}
                      >
                        Transcript
                      </button>
                      <button
                        type="button"
                        className="text-sm text-red-600"
                        onClick={() => handleRemoveStudent(student.id)}
                      >
                        Remove
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <h3 className="font-semibold text-neutral-900 mb-2">Enroll students</h3>
              <input
                className="input w-full mb-3"
                placeholder="Search students"
                value={studentSearch}
                onChange={(event) => setStudentSearch(event.target.value)}
              />
              <div className="max-h-48 overflow-y-auto border border-neutral-200 rounded-xl divide-y divide-neutral-100 mb-3">
                {enrollableStudents.slice(0, 40).map((student) => (
                  <label key={student.id} className="flex items-center gap-2 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selectedStudentIds.includes(student.id)}
                      onChange={() => {
                        setSelectedStudentIds((prev) => (
                          prev.includes(student.id)
                            ? prev.filter((id) => id !== student.id)
                            : [...prev, student.id]
                        ))
                      }}
                    />
                    <span>{student.name} {student.code ? `(${student.code})` : ''}</span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="btn-primary"
                onClick={handleEnrollSelected}
                disabled={isSaving || selectedStudentIds.length === 0}
              >
                Enroll selected
              </button>
            </div>
        </section>
      )}
    </div>
  )
}
